import { useState, useEffect } from "react";
import "./FormContentMultiple.css";
import { Question, UpdateQuestionPayload } from "effno-core";
import { apiUpdateQuestion } from "../../services/QuestionService";
import { toastFail, toastSuccess } from "../../config";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { selectEditingForm, selectEditingQuestionId, selectUIStates, setEditingForm } from "../../redux/features/appData";
import { useDispatch } from "react-redux";
import { MutlipleSettings } from "../config/questionSettings";
import { apiFormHasEdits } from "../../services/FormService";
import { CircleX, X } from "lucide-react";

type FormContentMultipleProps = {
    question: Question;
    allowSaving?: boolean;
}

export const FormContentMultiple = ({question, allowSaving = true} : FormContentMultipleProps) => {

    const dispatch = useDispatch();

    const editingForm = useSelector(selectEditingForm);
    const editingQuestionId = useSelector(selectEditingQuestionId);
    const uiStates = useSelector(selectUIStates);

    const [title, setTitle] = useState<string>("");
    const [description, setDescription] = useState<string>("");
    const [settings, setSettings] = useState<any | undefined>(undefined);
    const [choices, setChoices] = useState<string[]>([]);
    const [debouncedData, setDebouncedData] = useState<any | undefined>(undefined);
    const [ignoreFirstDebounce, setIgnoreFirstDebounce] = useState<boolean>(true);

    useEffect(() => {
        console.log("[FormContentMultiple] question changed")
        setTitle(question.title);
        setDescription(question.description);
        const questionSettings = JSON.parse(question.settings);
        setSettings(questionSettings);
        setChoices((questionSettings.choices && questionSettings.choices.length > 0) ? 
            [...questionSettings.choices] : ["First"]);
        setDebouncedData(undefined)
        setIgnoreFirstDebounce(true);
    }, [question])

    // Debounce effect to update debouncedData 1 second after the user stops typing
    useEffect(() => {
        if (ignoreFirstDebounce) {
            setIgnoreFirstDebounce(false);
            return;
        }

        console.log("[Settings changed]")
        const handler = setTimeout(() => {
            if (!settings) {
                return;
            }
            
            setDebouncedData({...settings, title, description, choices});
        }, 500);

        // Clear the timeout if settings changes (i.e., user is still typing)
        return () => {
            clearTimeout(handler);
        };
    }, [settings, title, description, choices]);

    // Effect to save the debounced data whenever it changes
    useEffect(() => {
        if (!debouncedData) {
            return;
        }
        console.log("[FormContentMultiple] debouncedData: ", debouncedData);
        handleSave();
    }, [debouncedData]);

    const createUpdatedQuestion = () : Question | null => {
        if (!editingForm) {
            return null;
        }

        const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        if (index === -1) {
            toast("Can't find question id", toastFail);
            return null;
        }

        // const newSettings: MutlipleSettings = {
        //     choices,
        //     multiSelectOn: debouncedData.multiSelectOn,
        //     randomizeOn: debouncedData.randomizeOn,
        //     hasOtherOn: debouncedData.hasOtherOn,
        //     otherValue: debouncedData.otherValue,
        //     enforceMinOn: debouncedData.enforceMinOn,
        //     minChoices: debouncedData.minSelection,
        //     enforceMaxOn: debouncedData.enforceMaxOn,
        //     maxChoices: debouncedData.maxSelection,
        // }

        const newSettings: MutlipleSettings = {
            choices,
            multiSelectOn: settings.multiSelectOn,
            randomizeOn: settings.randomizeOn,
            hasOtherOn: settings.hasOtherOn,
            otherValue: settings.otherValue,
            enforceMinOn: settings.enforceMinOn,
            minChoices: settings.minSelection,
            enforceMaxOn: settings.enforceMaxOn,
            maxChoices: settings.maxSelection,
        }

       return {...question,
            title,
            description,
            settings: JSON.stringify(newSettings)
        };
    }

    const updateRedux = (updatedQuestion: Question) => {
        if (!editingForm || !debouncedData) {
            return;
        }

        const index = editingForm.questions.findIndex((q: Question) =>  
            q.id === updatedQuestion.id);

        if (index === -1) {
            toast("Can't find question id to update", toastFail);
            return;
        }

        const newQuestions = [...editingForm.questions];
        newQuestions[index] = {...updatedQuestion}
        const newEditingForm = {...editingForm, questions: newQuestions, hasEdits: true};

        // make sure we do this first to keep up with the typing        
        dispatch(setEditingForm(newEditingForm));

        // await apiFormHasEdits(newEditingForm.id);
        // newEditingForm.hasEdits = true;
    }

    const updateQuestion = async (question: Question) => {
        if (!editingForm) {
            return;
        }
        
        const index = editingForm.questions.findIndex((q: Question) =>  q.id === question.id);

        if (index === -1) {
            toast("Can't find question id", toastFail);
            return;
        }

        const newQuestions = [...editingForm.questions];
        newQuestions[index] = {...question};
        const newEditingForm = {...editingForm, questions: newQuestions};

        // make sure we do this first to keep up with the typing        
        dispatch(setEditingForm(newEditingForm));

        await apiFormHasEdits(newEditingForm.id);
        newEditingForm.hasEdits = true;
    }
    
    const handleSave = async () => {
        await saveQuestion();
    }

    // function sleep(ms: number) {
    //     return new Promise(resolve => setTimeout(resolve, ms));
    // }

    const saveQuestion = async () => {
        console.log("SAVING!!!!!!", debouncedData)
        if (!editingForm || !allowSaving || !debouncedData) {
            return;
        }

        const updatedQuestion = createUpdatedQuestion();

        if (!updatedQuestion) {
            toast("Problem creating updated question.", toastFail);
            return;
        }

        updateRedux(updatedQuestion);
        setDebouncedData(undefined);

        // const newSettings: MutlipleSettings = {
        //     choices,
        //     multiSelectOn: settings.multiSelectOn,
        //     randomizeOn: settings.randomizeOn,
        //     hasOtherOn: settings.hasOtherOn,
        //     otherValue: settings.otherValue,
        //     enforceMinOn: settings.enforceMinOn,
        //     minChoices: settings.minSelection,
        //     enforceMaxOn: settings.enforceMaxOn,
        //     maxChoices: settings.maxSelection,
        // }

        const newSettings: MutlipleSettings = {
            choices,
            multiSelectOn: debouncedData.multiSelectOn,
            randomizeOn: debouncedData.randomizeOn,
            hasOtherOn: debouncedData.hasOtherOn,
            otherValue: debouncedData.otherValue,
            enforceMinOn: debouncedData.enforceMinOn,
            minChoices: debouncedData.minSelection,
            enforceMaxOn: debouncedData.enforceMaxOn,
            maxChoices: debouncedData.maxSelection,
        }

        const payload : UpdateQuestionPayload = {
            questionId: question.id,
            type: question.type,
            title: title,
            description: description,
            required: question.required,
            settings: JSON.stringify(newSettings),  // these don't change in this instance
            conditions: question.conditions
        }

        console.log("[FormContentMultiple/handleSave] payload: ", payload);

        // console.log("about to sleep");
        // await sleep(2000);
        // console.log("woke up");

        // NR we might need to change redux first

        const newQuestion = await apiUpdateQuestion(payload);

        if (!newQuestion) {
            toast("Error saving update", toastFail);
            return;
        }

        await apiFormHasEdits(editingForm.id);
        // newEditingForm.hasEdits = true;

        console.log("Question updated");
        // updateQuestion(newQuestion);
    }

    const handleDeleteChoice = async (index: number) => {
        const newChoices = [...choices]
        newChoices.splice(index, 1);
        setChoices(newChoices);
        await saveQuestion();

        // if (!editingForm) {
        //     return;
        // }

        // const questionIndex = editingForm.questions.findIndex((q: Question) =>  q.id === editingQuestionId);

        // if (questionIndex === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newSettings = {...settings, choices: newChoices};
        // const newQuestion = {
        //     ...editingForm.questions[questionIndex], 
        //     title,
        //     description,
        //     settings: JSON.stringify(newSettings)
        // };
        // updateQuestion(newQuestion);  
        // setDebouncedData(undefined)
    }

    const handleDeleteOther = async () => {
        const newSettings = {...settings, hasOtherOn: false, otherValue: ""};
        setSettings(newSettings);
        await saveQuestion();

        // if (!editingForm) {
        //     return;
        // }
        
        // const index = editingForm.questions.findIndex((q: Question) =>  q.id === editingQuestionId);

        // if (index === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newQuestion = {
        //     ...editingForm.questions[index], 
        //     title,
        //     description,
        //     settings: JSON.stringify(newSettings)
        // };

        // updateQuestion(newQuestion);  
        // setDebouncedData(undefined)
    }

    const handleUpdateChoice = (value: string, index: number) => {
        const newChoices = [...choices];
        newChoices[index] = value;
        setChoices(newChoices);

        // if (!editingForm) {
        //     return;
        // }

        // const questionIndex = editingForm.questions.findIndex((q: Question) =>  q.id === editingQuestionId);

        // if (questionIndex === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newSettings = {...settings, choices: newChoices};
       
        // const newQuestion = {
        //     ...editingForm.questions[questionIndex], 
        //     title,
        //     description,
        //     settings: JSON.stringify(newSettings)
        // };
        // updateQuestion(newQuestion);  
    }

    const handleAddChoice = async () => {
        const newChoices = [...choices, ""];
        setChoices(newChoices);
        await saveQuestion();

        // if (!editingForm) {
        //     return;
        // }

        // const questionIndex = editingForm.questions.findIndex((q: Question) =>  q.id === editingQuestionId);

        // if (questionIndex === -1) {
        //     toast("Can't find question id", toastFail);
        //     return;
        // }

        // const newSettings = {...settings, choices: newChoices};
        // const newQuestion = {
        //     ...editingForm.questions[questionIndex], 
        //     title,
        //     description,
        //     settings: JSON.stringify(newSettings)
        // };
        // updateQuestion(newQuestion);  
        // setDebouncedData(undefined)
    }

    const handleKeyDown = (e: any, index: number) => {
        console.log("[FormContentMultiple/handleKeyDown] index: ", index);
        if (e.key === "Enter") {
            if (index === choices.length - 1) {
                handleAddChoice();
            }
        }
    }

    // console.log("[FormContentMultiple] choices: ", choices);
    // console.log("[FormContentMultiple] settings: ", settings);

    return (
        <div className="form-content-body">
            <div className="form-content-title">
                <input 
                    type="text"
                    placeholder="..."
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    data-testid="question-title"
                />
            </div>
            <div className="form-content-description">
                <input 
                    type="text"
                    placeholder="Description (optional)"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    data-testid="question-description"
                />
            </div>   

            <div className="form-content-select">
                {choices && choices.map((choice: string, index: number) => {
                    return (
                        <div
                            key={"form-selection-" + index} 
                            className="form-content-select-item"
                        >
                            <input
                                type="text"
                                value={choice}
                                onChange={(e) => handleUpdateChoice(e.target.value, index)}
                                onKeyDown={(e) => handleKeyDown(e, index)}
                                data-testid={"choice#" + index}
                                autoFocus={!choice}
                            />
                            {choices.length > 1 &&
                                <div 
                                    className="form-content-select-item-delete"
                                    onClick={()=>handleDeleteChoice(index)}
                                >
                                    <CircleX size={20} color="black"/>
                                    {/* <img className="icon-close" /> */}
                                </div>
                            }
                        </div> 
                    )
                })}
                {settings && settings.hasOtherOn &&
                    <div className="form-content-select-item">
                         <input
                                type="text"
                                value="Other"
                                disabled={true}
                                data-testid={"choice#other"}
                        />
                        <div 
                            className="form-content-select-item-delete"
                            onClick={handleDeleteOther}
                        >
                            <img className="icon-close" />
                        </div>
                    </div>
                }

                <div className="form-content-select-add">
                    <a className="form-content-link" onClick={handleAddChoice}>Add Choice </a>
                </div>

                <div className="form-content-bottom-spacer">
                    &nbsp;
                </div>
                
            </div>

            {/* <button 
                onClick={handleSave}
                data-testid="save-question"
            >
                temp save button before debounce 
            </button> */}
        </div>
    )
}